import {
  clearStore,
  pdfStore,
  setDocument,
  setEventBus,
  setPage,
  setPdfFindController,
  setPdfLinkService,
  setPdfViewer,
  setTotalPages,
  usePdfViewerStore,
  scrollToSourceInPdf,
  highlightKeywordsInPdf,
} from "@/slices/pdfViewer";
import { BrokenImage } from "@mui/icons-material";
import { CircularProgress, Typography } from "@mui/material";
import { getDocument } from "pdfjs-dist";
import SANDBOX_BUNDLE_SRC from "pdfjs-dist/build/pdf.sandbox.mjs?url";
import "pdfjs-dist/web/pdf_viewer.css";
import {
  DownloadManager,
  EventBus,
  PDFFindController,
  PDFLinkService,
  PDFScriptingManager,
  PDFViewer,
} from "pdfjs-dist/web/pdf_viewer.mjs";
import { useEffect, useRef, useState } from "react";
import { useThrottledCallback } from "use-debounce";

/**
 * CustomPDFViewer component.
 *
 * @param {Object} props - Component props.
 * @param {string} props.fileUrl - The URL of the PDF file to be displayed.
 * @param {number|null} props.initialPage - Initial page number to scroll to (from URL params).
 * @param {string|null} props.initialHighlight - Initial text to highlight (from URL params).
 */
export default function CustomPDFViewer({
  fileUrl,
  initialPage = null,
  initialHighlight = null,
}) {
  const viewerRef = useRef(null);
  const viewerPagesContainerRef = useRef(null);
  const [isViewerLoaded, setIsViewerLoaded] = useState(false);
  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false);
  const [
    { status: isDocumentError, message: documentErrorMessage },
    setDocumentErrorState,
  ] = useState({
    status: false,
    message: "",
  });
  const { eventBus, pdfViewer, document } = pdfStore.getState();

  const throttledResizeHandler = useThrottledCallback(() => {
    if (pdfViewer == null) {
      return;
    }
    const currentScaleValue = pdfViewer.currentScaleValue;
    if (
      currentScaleValue === "auto" ||
      currentScaleValue === "page-fit" ||
      currentScaleValue === "page-width"
    ) {
      // Note: the scale is constant for 'page-actual'.
      pdfViewer.currentScaleValue = currentScaleValue;
    }

    pdfViewer.update();
  }, 1000);

  // Initialize the PDF viewer and related services
  useEffect(() => {
    function initializeViewer() {
      const eventBus = new EventBus();
      const pdfLinkService = new PDFLinkService({ eventBus });
      const pdfFindController = new PDFFindController({
        eventBus,
        linkService: pdfLinkService,
      });
      const pdfScriptingManager = new PDFScriptingManager({
        eventBus,
        sandboxBundleSrc: SANDBOX_BUNDLE_SRC,
      });
      const downloadManager = new DownloadManager();

      const pdfViewer = new PDFViewer({
        container: viewerRef.current,
        viewer: viewerPagesContainerRef.current,
        eventBus,
        linkService: pdfLinkService,
        findController: pdfFindController,
        scriptingManager: pdfScriptingManager,
        downloadManager: downloadManager,
      });
      pdfLinkService.setViewer(pdfViewer);
      pdfScriptingManager.setViewer(pdfViewer);

      // Stores - zustand
      setEventBus(eventBus);
      setPdfViewer(pdfViewer);
      setPdfLinkService(pdfLinkService);
      setPdfFindController(pdfFindController);

      setIsViewerLoaded(true);
    }
    if (viewerRef.current != null && viewerPagesContainerRef.current != null) {
      initializeViewer();
    }
    return () => {
      const { pdfViewer } = pdfStore.getState();
      if (pdfViewer) {
        pdfViewer.cleanup();
      }
    };
  }, []);

  // Load the PDF document when the fileUrl changes
  // and set the document in the store
  useEffect(() => {
    async function loadPdfDocument() {
      if (fileUrl == null || isViewerLoaded === false) {
        return;
      }

      setIsDocumentLoaded(false);
      setDocumentErrorState({
        message: "",
        status: false,
      });

      console.log("Fetching PDF from URL:", fileUrl);

      const loadingTask = getDocument({
        url: fileUrl,
        enableXfa: true,
        isEvalSupported: false,
        cMapUrl: window.location.origin + "/pdfjs-dist/cmaps/",
        cMapPacked: true,
        standardFontDataUrl:
          window.location.origin + "/pdfjs-dist/standard_fonts/",
        withCredentials: false,
      });

      try {
        const { pdfViewer, pdfLinkService } = pdfStore.getState();
        if (pdfViewer == null || pdfLinkService == null) {
          return;
        }
        const pdfDocument = await loadingTask.promise;
        pdfViewer.setDocument(pdfDocument);
        pdfLinkService.setDocument(pdfDocument, null);

        // Store zustand
        setDocument(pdfDocument);
        setTotalPages(pdfDocument.numPages);

        setIsDocumentLoaded(true);
      } catch (error) {
        console.error("Failed to load PDF:", error);
        const errorMessage = error?.message || "";
        const errorName = error?.name || "";
        const fullErrorString = JSON.stringify(error) || "";

        const isPdfMissing = errorName === "MissingPDFException";
        const isUnexpectedResponse = errorName === "UnexpectedResponseException";

        // Check for HTTP status codes in error message or full error
        const has403 =
          errorMessage.includes("403") ||
          fullErrorString.includes("403") ||
          errorMessage.toLowerCase().includes("authenticate") ||
          errorMessage.toLowerCase().includes("authorization") ||
          errorMessage.toLowerCase().includes("signature");

        const has401 =
          errorMessage.includes("401") ||
          fullErrorString.includes("401") ||
          errorMessage.toLowerCase().includes("unauthorized");

        const has404 =
          errorMessage.includes("404") ||
          fullErrorString.includes("404");

        // Check for CORS errors
        const isCorsError =
          errorMessage.toLowerCase().includes("cors") ||
          errorMessage.toLowerCase().includes("cross-origin") ||
          errorMessage.toLowerCase().includes("network error") ||
          errorMessage.toLowerCase().includes("failed to fetch") ||
          errorMessage.toLowerCase().includes("access-control") ||
          (errorMessage.includes("NetworkError") && !errorMessage.includes("404")) ||
          (errorName === "NetworkError" && !isPdfMissing && !has403 && !has401);

        // Check for other network errors
        const isNetworkError =
          (errorMessage.toLowerCase().includes("network") ||
            errorMessage.toLowerCase().includes("fetch") ||
            errorName === "NetworkError") && !has403 && !has401;

        const errorObj = {
          status: true,
          message: "Failed to load document.",
        };

        if (has403) {
          errorObj.message = "403 Forbidden: Access denied. The PDF URL may have expired (SAS token expired), the signature is invalid, or you don't have permission to access this file.";
        } else if (has401) {
          errorObj.message = "401 Unauthorized: Authentication required. Please check if the PDF URL requires authentication or if the access token has expired.";
        } else if (isPdfMissing || has404) {
          errorObj.message = "404 Error: Document not found. Either the document has been deleted or you don't have access to it.";
        } else if (isCorsError) {
          errorObj.message = "CORS Error: The server does not allow cross-origin requests. The PDF cannot be loaded due to CORS (Cross-Origin Resource Sharing) restrictions.";
        } else if (isNetworkError && !isCorsError) {
          errorObj.message = `Network Error: ${errorMessage || "Unable to connect to the server. Please check your internet connection."}`;
        } else if (isUnexpectedResponse) {
          errorObj.message = `Unexpected Response: ${errorMessage || "The server returned an unexpected response. This might be due to CORS restrictions, expired URL, or an invalid response from the server."}`;
        } else if (errorMessage) {
          errorObj.message = `Error: ${errorMessage}`;
        }

        setDocumentErrorState(errorObj);
        setIsDocumentLoaded(false);
      }
    }
    loadPdfDocument();
  }, [fileUrl, isViewerLoaded]);

  useEffect(() => {
    // Clear the store when the component unmounts
    return () => {
      clearStore();
    };
  }, []);

  useEffect(() => {
    // Get the stored values at initialization time
    const storedPage = usePdfViewerStore.getState().page;
    const storedSearch = usePdfViewerStore.getState().search;
    const storedZoom = usePdfViewerStore.getState().zoom;

    function onPagesLoaded() {
      // Apply stored zoom level in decimal format or text
      if (isNaN(storedZoom) === false) {
        pdfViewer.currentScale = storedZoom / 100;
      } else {
        pdfViewer.currentScaleValue = storedZoom;
      }

      // Apply stored page
      if (
        storedPage &&
        storedPage !== 1 &&
        parseInt(storedPage) <= document.numPages
      ) {
        pdfViewer.currentPageNumber = parseInt(storedPage);
      }

      // Apply stored search
      if (storedSearch && storedSearch.length > 0) {
        eventBus.dispatch("find", {
          query: storedSearch,
          phraseSearch: false,
          caseSensitive: false,
          entireWord: false,
          highlightAll: true,
        });
      }

      // Apply initial page and highlight from URL params after PDF is loaded
      // This happens after stored values are applied, so URL params take precedence
      if (initialPage && parseInt(initialPage) >= 1 && parseInt(initialPage) <= document.numPages) {
        // Use a small delay to ensure it happens after any initial pagechanging events
        setTimeout(() => {
          console.log("Scrolling to initial page from URL params:", initialPage);
          scrollToSourceInPdf(initialPage);
        }, 200);
      }

      if (initialHighlight && initialHighlight.length > 0) {
        // Use a small delay to ensure it happens after the page is set
        setTimeout(() => {
          console.log("Highlighting initial text from URL params:", initialHighlight);
          highlightKeywordsInPdf(initialHighlight);
        }, 300);
      }
    }
    if (eventBus != null && pdfViewer != null && document != null) {
      // Listen for the pagesloaded event to know when PDF is fully rendered
      eventBus.on("pagesloaded", onPagesLoaded);
      return () => {
        eventBus.off("pagesloaded", onPagesLoaded);
      };
    }
  }, [document, eventBus, pdfViewer, initialPage, initialHighlight]);

  // Check for Scroll and update the current page
  useEffect(() => {
    const updateScrollPages = (event) => {
      setPage(event.pageNumber);
    };
    if (eventBus != null) {
      eventBus.on("pagechanging", updateScrollPages);
      return () => {
        eventBus.off("pagechanging", updateScrollPages);
      };
    }
  }, [eventBus]);

  // Update the Scale level when resized
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (isDocumentLoaded && isViewerLoaded) {
        throttledResizeHandler();
      }
    });
    const viewer = viewerRef.current;
    if (viewer != null) {
      observer.observe(viewer);
      return () => {
        observer.unobserve(viewer);
      };
    }
  }, [isDocumentLoaded, isViewerLoaded, throttledResizeHandler]);

  return (
    <div id="pdf-container" className="relative flex-1">
      {isViewerLoaded === false && (
        <div className="bg-opacity-80 flex h-full w-full items-center justify-center gap-3">
          <CircularProgress size={15} />
          <Typography className="text-blue-300">
            Initializing Viewer...
          </Typography>
        </div>
      )}
      {isDocumentLoaded === false && !isDocumentError && (
        <div className="bg-opacity-80 flex h-full w-full items-center justify-center gap-3">
          <CircularProgress size={15} />
          <Typography className="text-blue-300">
            Please wait while we load your document...
          </Typography>
        </div>
      )}
      {isDocumentError && (
        <div className="bg-opacity-80 flex h-full items-center justify-center gap-3 bg-gray-100">
          <BrokenImage color="error" />
          <Typography className="text-blue-300" sx={{ maxWidth: '80%', textAlign: 'center' }}>
            {documentErrorMessage}
          </Typography>
        </div>
      )}
      <div
        ref={viewerRef}
        id="pdf-viewer"
        className="pdfViewer absolute h-full w-full overflow-auto"
      >
        <div ref={viewerPagesContainerRef}></div>
      </div>
    </div>
  );
}
